/* eslint camelcase: "off" */
import type {
  Operator,
  PolicyRequirement,
  RequirementsBlock,
} from '../../../../types/VerificationTypes';

/**
 * Whether a requirements block can be rendered as a single flat list
 * (no AND-of-ORs grouping). True when the block is empty or has a single
 * group whose operator matches the default. Used by both the editor and
 * the viewer to decide between flat-list and advanced layouts — keep
 * them sharing one definition so they don't drift apart.
 */
export function isFlatCompatible(
  value: RequirementsBlock | Record<string, never>,
  defaultOperator: Operator
): boolean {
  const groups = (value as RequirementsBlock).groups || [];
  if (groups.length === 0) return true;
  return groups.length === 1 && groups[0].operator === defaultOperator;
}

/**
 * Build the inverse of `PolicyRequirement.satisfies`: for each requirement
 * code C, which OTHER requirements (if held) would also satisfy a policy
 * asking for C. Inverts every entry in every requirement's `satisfies`
 * list — so a stronger credential like `whatsapp_id_verified` (whose
 * `satisfies` lists `whatsapp_id_uploaded`) becomes the "satisfied by"
 * for `whatsapp_id_uploaded`.
 *
 * The forward direction is stored on the model; the inverse is recomputed
 * on every page load. Always cheap — the registry has <100 rows in
 * production and `satisfies` is sparse.
 */
export function buildSatisfiedByMap(
  requirements: PolicyRequirement[]
): Map<string, PolicyRequirement[]> {
  const byTarget = new Map<string, PolicyRequirement[]>();
  requirements.forEach((requirement) => {
    (requirement.satisfies || []).forEach((target) => {
      const list = byTarget.get(target) || [];
      list.push(requirement);
      byTarget.set(target, list);
    });
  });
  return byTarget;
}
